// Per-job ACL ("sharing") dbaccess helpers — mirror admin-rpc-client's
// dbaccess usage (no dedicated RPC). Writes/reads continuum.job_auth.
//
// A job is an execute-code-on-a-worker capability, so create/delete stay
// admin-only; sharing delegates USE of an existing job at a named level
// (viewer < operator < editor < manager). The firewall gates the write:
// ContinuumAdmin today; non-admin managers once the manager-write gate lands.
import { adminRpc } from "./admin-rpc-client";
import { uuidv7 } from "uuidv7";

export const JOB_SHARE_LEVELS = [
  "viewer",
  "operator",
  "editor",
  "manager",
] as const;
export type JobShareLevel = (typeof JOB_SHARE_LEVELS)[number];

export interface JobAccessEntry {
  usergroupUid: string;
  name: string;
  perms: string;
}

function esc(s: string): string {
  return s.replace(/'/g, "''");
}

function freshUid(): string {
  return uuidv7().replaceAll("-", "");
}

// listJobAccess returns the active grants on a job (who-has-access), resolving
// usergroup names. Firewall-scoped: a manager sees their job's grants; an admin
// sees all.
export async function listJobAccess(jobUid: string): Promise<JobAccessEntry[]> {
  const r = await adminRpc.query(`
    SELECT ja.usergroup_uid AS usergroup_uid, g.name AS name, ja.perms AS perms
      FROM job_auth ja JOIN usergroup g ON g.uid = ja.usergroup_uid
     WHERE ja.job_uid = '${esc(jobUid)}' AND ja.status = 'active'
     ORDER BY g.name
  `);
  return (r.rows ?? []).map((row) => ({
    usergroupUid: row.usergroup_uid as string,
    name: row.name as string,
    perms: row.perms as string,
  }));
}

// shareJob grants (or changes) a usergroup's level on a job. A single upsert
// keyed on the natural key (job_uid, usergroup_uid): if a row exists — active OR
// previously-revoked (status='inactive') — it is updated to perms=level,
// status='active' (reactivating a revoked grant); otherwise a fresh row is
// inserted. dbaccess.v1.Upsert's lookup has no status filter, so this avoids the
// "duplicate key" the old query-active-then-insert path hit when re-granting a
// previously-revoked user (the soft-deleted row still occupies the unique index).
export async function shareJob(
  jobUid: string,
  usergroupUid: string,
  level: JobShareLevel,
): Promise<void> {
  const resp = await adminRpc.upsert(
    "job_auth",
    ["job_uid", "usergroup_uid"],
    {
      // uid is used only on the insert path; the update/reactivate path leaves
      // the existing row's PK untouched (DataUpsert strips PK from the SET list).
      uid: freshUid(),
      job_uid: jobUid,
      usergroup_uid: usergroupUid,
      perms: level,
      status: "active",
    },
  );
  if (!resp.success) {
    throw new Error(resp.error?.message || "failed to grant access");
  }
}

// unshareJob revokes a usergroup's access on a job (soft-delete).
export async function unshareJob(
  jobUid: string,
  usergroupUid: string,
): Promise<void> {
  const existing = await adminRpc.query(`
    SELECT uid FROM job_auth
     WHERE job_uid = '${esc(jobUid)}' AND usergroup_uid = '${esc(usergroupUid)}'
       AND status = 'active' LIMIT 1
  `);
  if ((existing.rows ?? []).length === 0) return;
  const uid = existing.rows[0].uid as string;
  const resp = await adminRpc.update("job_auth", { uid }, { status: "inactive" });
  if (!resp.success) {
    throw new Error(resp.error?.message || "failed to revoke access");
  }
}
